<script lang="ts">
  import { get } from "svelte/store";
  import { getProjectsByCustomer, getCustomer } from "../../../services/api/data-handler.js";
  import type { Project, ProjectsData } from "../../../services/api/types.js";
  import { UserType } from "../../../services/api/types.js";
  import { editObject } from "../../../services/stores/object-drawer-store.js";
  import { onMount } from "svelte";
  import { createGrid } from "ag-grid-community";
  import type { GridOptions, GridApi } from "ag-grid-community";
  import { navigateTo } from "../../../services/routing/route-action.js";
  import { theme } from "../../../services/stores/theme-store.js";
  import GridHeader from "../../common/GridHeader.svelte";
  import { getSVGIcon } from "../../../services/utils.js";
  import DeleteObjectDialog from "../../dialogs/DeleteObjectDialog.svelte";
  import FileUploadDrawer from "../../drawers/FileUploadDrawer.svelte";
  import { userStore } from "../../../services/stores/user-store.js";
  import { drawerStore } from "../../../services/stores/object-drawer-store.js";
  import { formatDate, userRole, userType } from "../../../helpers/helpers.js";
  import { uploadStore } from "../../../services/stores/upload-store.js";

  export let customer_id: string;

  let deleteDialogOpen = false;
  let objectToDelete: any = null;

  let gridOptions: GridOptions;
  let gridApi: GridApi;

  let projectsData: ProjectsData;
  let projects: Project[] = [];
  let canManageProjects = false;
  let canLoadProjectDocuments = false;
  let canViewCodes = false;
  
  let uploadDrawerHidden = true;

  let selectedProjectId: string;
  let customerInactive = false;

  // Smart refresh interval managed at module scope
  let refreshInterval: ReturnType<typeof setInterval> | undefined = undefined;

  function hasActiveProcessing(list: Project[]): boolean {
    if (!Array.isArray(list)) return false;
    return list.some((p: any) => {
      const status = p?.last_upload?.status;
      return status === "processing" || status === "started" || status === "uploaded";
    });
  }

  function startSmartInterval() {
    if (refreshInterval) return;
    refreshInterval = setInterval(() => {
      loadProjects();
    }, 3000);
  }

  function stopSmartInterval() {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = undefined;
    }
  }
 
  async function loadProjects() {
    projectsData = await getProjectsByCustomer(customer_id);
    projects = projectsData.projects;
    updateGridData();
    if (document.hidden) {
      stopSmartInterval();
    } else if (hasActiveProcessing(projects)) {
      startSmartInterval();
    } else {
      stopSmartInterval();
    }
  }

  function updateGridData() {
    if (gridApi) {
      gridApi.setGridOption("rowData", projects);
      setTimeout(() => {
        gridApi.sizeColumnsToFit();
        gridApi.autoSizeAllColumns();
      }, 100);
    }
  }

  // Update theme mode on the grid container when theme changes
  $: {
    const gridElement = document.querySelector("#projectGrid");
    if (gridElement) {
      gridElement.setAttribute("data-ag-theme-mode", $theme);
    }
  }

  $: if ($drawerStore.object && $drawerStore.objectType === "project") {
    loadProjects();
  }

  // Refresh upload-button enable/disable when this customer's status changes via drawer
  $: if (
    $drawerStore.object &&
    $drawerStore.objectType === "customer" &&
    ($drawerStore.id === customer_id || $drawerStore.object?.customer_id === customer_id)
  ) {
    updateCustomerStatusAndRefresh();
  }

  onMount(() => {
    
    const store = get(userStore);
    canManageProjects = store.claims?.canManageProjects ?? false;
    canLoadProjectDocuments = store.claims?.canLoadProjectDocuments ?? false;
    canViewCodes = store.claims?.canViewCodes ?? false;

    let unsubscribeUpload: (() => void) | undefined = undefined;

    // Fetch customer status once to control upload button enabled/disabled
    updateCustomerStatusAndRefresh();

    // Initialize grid first
    gridOptions = {
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
      autoSizeStrategy: {
        type: "fitCellContents",
      },
      columnDefs: [
        {
          field: "name",
          headerName: "Name",
          cellRenderer: (params: any) => {
            return createNameCell(params);
          },
        },
        ...(canViewCodes
          ? [
            {
              field: "project_id",
              headerName: "Project ID",
              hide: true,
            }
            ]
          : []),
        {
          field: "current_upload.document_count",
          headerName: "Loaded Documents",
          filter: false,
          sortable: true,
          cellRenderer: (params: any) => {
            return createLoadedDocumentsCell(params);
          },
        },
        {
          field: "upload",
          headerName: "Processing Documents",
          filter: false,
          sortable: false,
          cellRenderer: (params: any) => {
            return createUploadedDocumentsCell(params);
          },
        },
        {
          field: "metadata.project_status.name",
          headerName: "Status",
          enableRowGroup: true,
          cellRenderer: (params: any) => {
            return createStatusCell(params);
          },
          filter: "agSetColumnFilter",
          filterParams: {
            excelMode: "mac",
          },
        },
        {
          field: "last_modified",
          headerName: "Last Modified",
          filter: false,
          sortable: false,
          cellRenderer: (params: any) => {
            return createModifiedCell(params, []);
          },
        }
      ],
      groupDisplayType: "groupRows",
      rowGroupPanelShow: "always",
      onGridReady: (params) => {
        updateGridData();
      },
      noRowsOverlayComponent: "customNoRowsOverlay",
      noRowsOverlayComponentParams: {
        message: "No projects found",
      },
    };

    const gridElement = document.querySelector("#projectGrid") as HTMLElement;
    gridApi = createGrid(gridElement, gridOptions);

    gridElement.addEventListener("click", (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "A") {
        event.preventDefault();
        const project_id = target.getAttribute("data-project-id");
        const customer_id = target.getAttribute("data-customer-id");
        if (project_id) {
          onOpenProjectClick(project_id);
        } else if (customer_id) {
          onOpenCustomerClick(customer_id);
        }
      }
    });

    // Load data after grid is initialized
    loadProjects();

    // React to local upload lifecycle to refresh more responsively
    unsubscribeUpload = uploadStore.subscribe((state) => {
      if (state.status === "uploading") {
        startSmartInterval();
      } else if (state.status === "completed" || state.status === "failed") {
        // Ensure we pick up server-side processing state transitions
        loadProjects();
      }
    });

    // Pause polling when tab is hidden; refresh once visible
    const onVisibilityChange = () => {
      if (document.hidden) {
        stopSmartInterval();
      } else {
        loadProjects();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (unsubscribeUpload) unsubscribeUpload();
    };
  });

  function onOpenProjectClick(project_id: string) {
    navigateTo("project", project_id);
  }

  function onOpenCustomerClick(customer_id: string) {
    navigateTo("customer", customer_id);
  }

  function onDeleteClick(project_id: string) {
    objectToDelete = projects.find((s) => s.project_id === project_id);
    if (objectToDelete) {
      deleteDialogOpen = true;
    }
  }

  $: if (objectToDelete) {
  }

  function onEditClick(project_id: string) {
    editObject("project", project_id);
  }

  function onUploadClick(project_id: string) {
    selectedProjectId = project_id;
    uploadDrawerHidden = false;
  }

  function createNameCell(params: any) {
    const name = `<a href="#" data-project-id="${params.data.project_id}">${params.data.name}</a>`;
    const project_uploads = params.data.project_uploads_count;
    const span = document.createElement("span");
    span.classList.add("grid-cell");
    span.innerHTML = name;

    const buttonConfigs = [
      {
        type: "edit",
        icon: "edit",
        onClick: onEditClick,
        isVisible: (data: any) => {
          return canManageProjects;
        },
      },
      {
        type: "delete",
        icon: "delete",
        onClick: onDeleteClick,
        isVisible: () => {
          return (
            userRole() === "admin" ||
            (canManageProjects && project_uploads === 0)
          );
        },
      },
    ];

    // Filter buttons based on visibility rules
    const visibleButtons = buttonConfigs.filter((btn) =>
      btn.isVisible(params.data)
    );

    // Only create button group if there are visible buttons
    if (visibleButtons.length > 0) {
      const buttonGroup = document.createElement("span");
      buttonGroup.classList.add("grid-button-group");
      addActionButtons(buttonGroup, params, visibleButtons);
      span.appendChild(buttonGroup);
    }
    return span;
  }

  function createStatusCell(params: any) {
    const status = formatToProperCase(params.data.metadata.project_status.name);
    const span = document.createElement("span");
    const span1 = document.createElement("span");
    span.classList.add("grid-cell", "status-container");
    span1.classList.add("status", status);
    span1.innerHTML = status;
    span.appendChild(span1);
    return span;
  }

  function createLoadedDocumentsCell(params: any) {
    const span = document.createElement("span");
    span.classList.add("grid-cell");
    if (params.data.current_upload.document_count > 0) {
      const subcell = document.createElement("span");
      subcell.classList.add("grid-subcell");
      subcell.appendChild(
        createSpan(`${params.data.current_upload.document_count}`, "count")
      );
      subcell.appendChild(
        createSpan(`v${params.data.current_upload.version}`, "version")
      );
      subcell.appendChild(
        createSpan(
          `${params.data.current_upload.username} (${formatDate(params.data.current_upload.date)})`,
          "meta"
        )
      );
      subcell.appendChild(createSuccessIcon());
      span.appendChild(subcell);
    } else {
      span.innerHTML = "None";
    }

    return span;
  }

  function createUploadedDocumentsCell(params: any) {
    const span = document.createElement("span");
    span.classList.add("grid-cell");
    if (params.data.last_upload.version > 0) {
      if (params.data.last_upload.status === "finished") {
        span.innerHTML = "None";
      } else {
        const subcell = document.createElement("span");
        subcell.classList.add("grid-subcell");
        subcell.appendChild(
          createSpan(`${params.data.last_upload.document_count}`, "count")
        );
        subcell.appendChild(
          createSpan(`v${params.data.last_upload.version}`, "version")
        );
        subcell.appendChild(
          createSpan(
            `${params.data.last_upload.username} (${formatDate(params.data.last_upload.date)})`,
            "meta"
          )
        );
        const uploadDate = new Date(params.data.last_upload.date);
        if (params.data.last_upload.status === "processing") {
          const pastTime = new Date();
          pastTime.setHours(pastTime.getHours() - 12);
          if (uploadDate < pastTime) {
            subcell.appendChild(
              createWarningIcon("Failed to finish process", "--red-600")
            );
          } else {
            subcell.appendChild(createSpinnerIcon());
          }
        } else if (params.data.last_upload.status === "failed") {
          subcell.appendChild(
            createWarningIcon(
              params.data.last_upload.error?.message,
              "--red-600"
            )
          );
        } else if (params.data.last_upload.status === "started") {
          const pastTime = new Date();
          pastTime.setHours(pastTime.getHours() - 0.25);
          if (uploadDate < pastTime) {
            subcell.appendChild(
              createWarningIcon("Upload failed", "--red-600")
            );
          } else {
            subcell.appendChild(createSpinnerIcon());
          }
        } else if (params.data.last_upload.status === "uploaded") {
          const pastTime = new Date();
          pastTime.setHours(pastTime.getHours() - 12);
          if (uploadDate < pastTime) {
            subcell.appendChild(
              createWarningIcon("Failed to start process", "--red-600")
            );
          } else {
            subcell.appendChild(createSpinnerIcon());
          }
        }
        span.appendChild(subcell);
      }
    } else {
      span.innerHTML = "None";
    }
    const buttonConfigs = [
      {
        icon: "upload",
        onClick: onUploadClick,
        isVisible: () => {
          return canLoadProjectDocuments;
        },
        isDisabled: () => {
          return customerInactive;
        },
      },
    ];

    const visibleButtons = buttonConfigs.filter((btn) => btn.isVisible());

    if (visibleButtons.length > 0) {
      const buttonGroup = document.createElement("span");
      buttonGroup.classList.add("grid-button-group");
      addActionButtons(buttonGroup, params, visibleButtons);
      span.appendChild(buttonGroup);
    }
    return span;
  }

  async function updateCustomerStatusAndRefresh() {
    try {
      const data = await getCustomer(customer_id);
      const status = data.customer?.metadata?.customer_status?.name || "";
      customerInactive = status.toLowerCase() === "inactive";
    } catch (e) {
      customerInactive = false;
    } finally {
      if (gridApi) {
        // Force cells to re-render to reflect button disabled state
        gridApi.setGridOption("rowData", [...projects]);
      }
    }
  }
  function createModifiedCell(params: any, buttonConfigs: any) {
    const span = document.createElement("span");
    span.classList.add("grid-cell", "modified-cell");
    const content = document.createElement("div");

    if (!params.data.updated_at) {
      span.classList.add("created");
      const date = formatDate(params.data.created_at);
      content.textContent = `${params.data.created_by_name} (${date})`;
    } else {
      span.classList.add("updated");
      const date = formatDate(params.data.updated_at);
      content.textContent = `${params.data.updated_by_name} (${date})`;
    }
    span.appendChild(content);
    return span;
  }

  function addActionButtons(span: any, params: any, buttonConfigs: any) {
    buttonConfigs.forEach((config: any) => {
      const button = document.createElement("button");
      button.classList.add("grid-button", `${config.type}-button`);
      button.innerHTML = getSVGIcon(config.icon);
      if (config.text) {
        button.innerHTML += config.text;
        button.classList.add("text");
      }
      const disabled = typeof config.isDisabled === "function" ? !!config.isDisabled() : !!config.disabled;
      if (disabled) {
        button.disabled = true;
        button.classList.add("disabled");
        if (!button.title) {
          button.title = "Uploads disabled: customer is Inactive";
        }
      }
      button.addEventListener("click", () =>
        config.onClick(params.data.project_id)
      );
      span.appendChild(button);
    });
    return span;
  }

  function createSpan(value: string, className: string) {
    const span = document.createElement("span");
    span.classList.add(className);
    span.innerHTML = value;
    return span;
  }

  function createWarningIcon(message: string, color: string): HTMLElement {
    const warningIcon = document.createElement("i");
    warningIcon.className = "fas fa-warning";
    warningIcon.style.fontSize = "16px";
    warningIcon.style.color = `var(${color})`;
    warningIcon.title = message;
    return warningIcon;
  }

  function createSpinnerIcon(): HTMLElement {
    const spinnerIcon = document.createElement("i");
    spinnerIcon.className = "fas fa-spinner fa-spin";
    spinnerIcon.style.fontSize = "16px";
    spinnerIcon.style.color = "var(--primary-600)";
    return spinnerIcon;
  }

  function createSuccessIcon(): HTMLElement {
    const successIcon = document.createElement("i");
    successIcon.className = "fas fa-check";
    successIcon.style.fontSize = "16px";
    successIcon.style.color = "var(--green-600)";
    return successIcon;
  }

  function formatToProperCase(text: string): string {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }
</script>

<svelte:head>
  <link
    rel="stylesheet"
    href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css"
  />
</svelte:head>

<GridHeader
  title="Projects"
  objectType="project"
  parentId={customer_id}
  on:refresh={() => loadProjects()}
  canAdd={canManageProjects}
/>
<div id="projectGrid" class="ag-theme-quartz ag-grid" data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"></div>
<DeleteObjectDialog
  bind:open={deleteDialogOpen}
  objectType="project"
  object={objectToDelete}
  on:delete={async (event) => {
    if (event.detail?.result) {
      projects = projects.filter(
        (p) => p.project_id !== objectToDelete.project_id
      );
      updateGridData();
      deleteDialogOpen = false;
      objectToDelete = null;
    }
  }}
  on:cancel={() => {
    deleteDialogOpen = false;
    objectToDelete = null;
  }}
/>

<FileUploadDrawer
  bind:hidden={uploadDrawerHidden}
  project_id={selectedProjectId}
/>

